import { useState, useEffect, useRef, useCallback } from 'react';
import PusherService, { type RealtimeChannel } from '../services/PusherService';

export interface JobStatusUpdate {
  jobId: string;
  userId: string;
  status: string;
  timestamp: string;
  progress?: number;
  currentStep?: string;
  outputLocation?: string;
  // 🆕 Optional multi-file support (backward compatible)
  outputFiles?: Array<{ label: string; key: string }>;
  manifestKey?: string;
  outputBundlePrefix?: string;
  errorMessage?: string;
  thinkingText?: string;
  isThinkingUpdate?: boolean;
  markdownContent?: string;
  contentType?: string;
  isMarkdownPreview?: boolean;
  
  // Document processing update fields
  processingText?: string;
  isDocumentProcessingUpdate?: boolean;
  currentPhase?: string; // e.g., "File 1/3: contract.pdf"
  operationStatus?: string; // e.g., "COMPLETED" for hiding the section
  
  // New streaming fields
  streamingType?: 'progress' | 'content' | 'section' | 'complete' | 'error';
  streamingContent?: string;
  streamingSection?: string;
  isStreamingUpdate?: boolean;
  isStreamingChunk?: boolean;
  streamingChunk?: StreamingChunk;
}

export interface StreamingChunk {
  chunkId: string;
  jobId: string;
  userId: string;
  chunkIndex: number;
  totalChunks: number;
  content: string;
  section?: string;
  isComplete: boolean;
  timestamp: string;
}

interface UseJobStatusUpdatesOptions {
  userId: string | null;
  jobId?: string | null;
  getAuthToken: () => Promise<string | null>;
  onStatusUpdate?: (update: JobStatusUpdate) => void;
}

interface UseJobStatusUpdatesOutput {
  isConnected: boolean;
  activeChannels: string[];
  debugInfo: any;
}

/**
 * Hook for job status updates using singleton Pusher service
 * This prevents multiple Pusher connections per user
 */
const useJobStatusUpdates = ({
  userId,
  jobId,
  getAuthToken,
  onStatusUpdate
}: UseJobStatusUpdatesOptions): UseJobStatusUpdatesOutput => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [activeChannels, setActiveChannels] = useState<string[]>([]);
  const [debugInfo, setDebugInfo] = useState<any>({});
  
  const channelRef = useRef<RealtimeChannel | null>(null);
  const callbackRef = useRef<((data: JobStatusUpdate) => void) | null>(null);
  const currentChannelName = useRef<string | null>(null);
  const statusHandlerRef = useRef<((data: JobStatusUpdate) => void) | undefined>(undefined);

  // Update connection status
  const updateConnectionStatus = useCallback(() => {
    const status = PusherService.getConnectionStatus();
    const debug = PusherService.getDebugInfo();
    
    setIsConnected(status.isConnected);
    setActiveChannels(debug.activeChannels);
    setDebugInfo(debug);
  }, []);

  useEffect(() => {
    statusHandlerRef.current = onStatusUpdate;
  }, [onStatusUpdate]);

  // Keep the subscription callback stable so we don't resubscribe on every render.
  const handleStatusUpdate = useCallback((data: JobStatusUpdate) => {
    statusHandlerRef.current?.(data);
  }, []);

  useEffect(() => {
    if (!userId) {
      console.warn('⚠️ useJobStatusUpdates: No userId provided, skipping Pusher setup');
      return;
    }

    const channelName = `private-job-${userId}`;
    const eventName = 'job-status-update';

    let mounted = true;

    const setupPusherConnection = async () => {
      try {
        console.log('🔄 Setting up Pusher connection for user:', userId);
        
        // Initialize Pusher service (singleton)
        await PusherService.initialize(getAuthToken);
        
        // Subscribe to the user's private channel
        const channel = await PusherService.subscribe(channelName, getAuthToken);
        
        if (!channel || !mounted) {
          console.warn('⚠️ Failed to subscribe to channel or component unmounted');
          return;
        }

        // Bind to job status updates
        await PusherService.bind(channelName, eventName, handleStatusUpdate, getAuthToken);
        
        // Store references for cleanup
        channelRef.current = channel;
        callbackRef.current = handleStatusUpdate;
        currentChannelName.current = channelName;
        
        console.log('✅ Successfully set up Pusher connection for channel:', channelName);
        
        // Update connection status
        updateConnectionStatus();
        
        // Set up periodic status updates
        const statusInterval = setInterval(updateConnectionStatus, 5000);
        
        return () => {
          clearInterval(statusInterval);
        };
        
      } catch (error) {
        console.error('❌ Failed to set up Pusher connection:', error);
      }
    };

    setupPusherConnection();

    return () => {
      mounted = false;
      
      // Clean up subscriptions
      if (currentChannelName.current && callbackRef.current) {
        console.log('🧹 Cleaning up Pusher subscription for channel:', currentChannelName.current);
        PusherService.unbind(currentChannelName.current, eventName, callbackRef.current);
        
        // 🚀 NEW: Auto-disconnect Pusher when no active subscriptions remain
        setTimeout(() => {
          const remainingSubscriptions = PusherService.getActiveChannelCount();
          if (remainingSubscriptions === 0) {
            console.log('🔌 No active Pusher subscriptions remaining, disconnecting to save resources');
            PusherService.cleanup();
          } else {
            console.log(`🔗 ${remainingSubscriptions} Pusher subscriptions still active, keeping connection`);
          }
        }, 1000); // Small delay to allow other hooks to clean up
      }
      
      // Reset refs
      channelRef.current = null;
      callbackRef.current = null;
      currentChannelName.current = null;
    };
  }, [userId, getAuthToken, handleStatusUpdate, updateConnectionStatus]);

  // Update debug info periodically
  useEffect(() => {
    const debugInterval = setInterval(updateConnectionStatus, 10000);
    return () => clearInterval(debugInterval);
  }, [updateConnectionStatus]);

  // 🚀 NEW: Auto-disconnect when page becomes hidden (browser tab minimized/hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTimeout(() => {
          const remainingSubscriptions = PusherService.getActiveChannelCount();
          if (remainingSubscriptions === 0) {
            console.log('🌙 Page hidden with no active subscriptions, disconnecting Pusher to save resources');
            PusherService.cleanup();
          }
        }, 5000); // 5 second delay in case user quickly switches back
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return {
    isConnected,
    activeChannels,
    debugInfo
  };
};

export default useJobStatusUpdates; 